import React, { useEffect, useRef, useState } from 'react';
import { Plus, X, Loader2 } from 'lucide-react';
import { Input } from '../ui/Input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/Select';
import commandCenterService from '../../services/commandCenterService';

// Module-level cache so hospitals load once and never flicker on form re-renders
let cachedHospitals = null;
let hospitalsFetchPromise = null;

const CommandCenterHospitalField = ({
  value,
  onChange,
  onAction,
  onError,
  label = 'Partner Hospital',
  placeholder = 'Select partner hospital',
}) => {
  const [hospitals, setHospitals] = useState(() => cachedHospitals || []);
  const [showAddNew, setShowAddNew] = useState(false);
  const [newHospital, setNewHospital] = useState({ name: '', address: '', city: '', state: '' });
  const [submittingHospital, setSubmittingHospital] = useState(false);
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    let cancelled = false;

    if (cachedHospitals) {
      setHospitals(cachedHospitals);
      return;
    }

    if (!hospitalsFetchPromise) {
      hospitalsFetchPromise = commandCenterService.getHospitals({ limit: 100 });
    }

    hospitalsFetchPromise
      .then((result) => {
        const list = Array.isArray(result?.data) ? result.data : [];
        cachedHospitals = list;
        if (!cancelled) {
          setHospitals(list);
        }
      })
      .catch((error) => {
        hospitalsFetchPromise = null;
        if (!cancelled) {
          onErrorRef.current?.(error.message || 'Failed to load partner hospitals');
        }
      });

    return () => {
      cancelled = true;
    };
  }, []); // Run strictly once on mount

  const currentHospitalName = value?.name || '';

  const handleSelectChange = (val) => {
    if (!val || val === '__none__') {
      onChange?.({ id: null, name: '' });
      return;
    }
    const matched = hospitals.find((h) => h.name === val || (h.hospital_id && h.hospital_id === val));
    onChange?.({
      id: matched ? (matched.hospital_id || matched.id) : null,
      name: matched ? matched.name : val,
    });
  };

  const updateNewHospital = (key, val) => setNewHospital((prev) => ({ ...prev, [key]: val }));

  const handleSubmitNewHospital = async () => {
    if (!newHospital.name.trim()) return;
    setSubmittingHospital(true);
    try {
      await commandCenterService.requestHospitalRegistration(newHospital);
      onAction?.(`Hospital registration request sent to Admin for review: ${newHospital.name.trim()}`);
      onChange?.({ id: null, name: newHospital.name.trim() });
      setShowAddNew(false);
      setNewHospital({ name: '', address: '', city: '', state: '' });
    } catch (error) {
      onErrorRef.current?.(error.message || 'Failed to submit hospital registration request');
    } finally {
      setSubmittingHospital(false);
    }
  };

  // Check if current value exists in hospitals list; if not but value exists (e.g. from existing booking), preserve it
  const isCustomOrLegacy =
    Boolean(currentHospitalName) &&
    !hospitals.some((h) => h.name.toLowerCase() === currentHospitalName.toLowerCase());

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-xs font-semibold text-gray-700 block">{label}</label>
        <button
          type="button"
          onClick={() => setShowAddNew(true)}
          className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 hover:underline cursor-pointer"
        >
          <Plus className="w-3.5 h-3.5" />
          Add Hospital
        </button>
      </div>

      <Select
        value={currentHospitalName || ''}
        onValueChange={handleSelectChange}
      >
        <SelectTrigger className="w-full h-9 sm:h-10 text-xs sm:text-sm">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className="max-h-60">
          <SelectItem value="__none__">
            <span className="text-gray-400 italic">None (No Partner Hospital)</span>
          </SelectItem>
          {isCustomOrLegacy && (
            <SelectItem value={currentHospitalName}>
              <span className="font-medium text-gray-900">{currentHospitalName}</span>
            </SelectItem>
          )}
          {hospitals.map((h) => {
            const id = h.hospital_id || h.id || h.name;
            return (
              <SelectItem key={id} value={h.name}>
                <div className="flex items-center justify-between w-full gap-2">
                  <span className="truncate">{h.name}</span>
                  {h.city && (
                    <span className="text-gray-400 text-[10px] shrink-0 capitalize">
                      {h.city}
                    </span>
                  )}
                </div>
              </SelectItem>
            );
          })}
          {hospitals.length === 0 && !isCustomOrLegacy && (
            <div className="px-3 py-3 text-xs text-gray-400 text-center">
              No partner hospitals available
            </div>
          )}
        </SelectContent>
      </Select>

      {/* Add New Hospital Modal */}
      {showAddNew && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 animate-in fade-in-0 duration-150"
          onClick={() => setShowAddNew(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900">Add New Hospital</h3>
              <button
                type="button"
                onClick={() => setShowAddNew(false)}
                className="w-7 h-7 flex items-center justify-center rounded-md border border-gray-200 text-gray-500 hover:bg-gray-50 cursor-pointer"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="px-5 py-4 space-y-3 text-sm">
              <p className="text-xs text-gray-500">
                This sends a request for the Admin to add this hospital — it won&rsquo;t appear in the list until approved.
              </p>
              <div>
                <label className="text-xs font-semibold text-gray-700 mb-1 block">
                  Hospital Name <span className="text-red-500">*</span>
                </label>
                <Input
                  value={newHospital.name}
                  onChange={(e) => updateNewHospital('name', e.target.value)}
                  placeholder="e.g. Civil Hospital"
                  className="h-9 text-xs sm:text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-700 mb-1 block">Hospital Address</label>
                <Input
                  value={newHospital.address}
                  onChange={(e) => updateNewHospital('address', e.target.value)}
                  placeholder="Street address"
                  className="h-9 text-xs sm:text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-700 mb-1 block">City</label>
                  <Input
                    value={newHospital.city}
                    onChange={(e) => updateNewHospital('city', e.target.value)}
                    placeholder="City"
                    className="h-9 text-xs sm:text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-700 mb-1 block">State</label>
                  <Input
                    value={newHospital.state}
                    onChange={(e) => updateNewHospital('state', e.target.value)}
                    placeholder="State"
                    className="h-9 text-xs sm:text-sm"
                  />
                </div>
              </div>
            </div>
            <div className="px-5 py-4 border-t border-gray-100 bg-gray-50/50 flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setShowAddNew(false)}
                className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-100 text-xs sm:text-sm font-medium transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!newHospital.name.trim() || submittingHospital}
                onClick={handleSubmitNewHospital}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-primary hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed text-primary-foreground text-xs sm:text-sm font-semibold transition-colors cursor-pointer"
              >
                {submittingHospital && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Send Request to Admin
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommandCenterHospitalField;
